import { useMemo } from 'react';
import { useAppStore } from '../store';
import { useReportStore, type DateRange } from '../store/reportStore';
import {
  getRootCategories,
  getCategoryDescendants,
  getTargetCategoryId,
} from '../services/categoryService';
import { CHART_COLOR_PALETTE } from '../components/charts/constants';

export interface MonthlyRootCategorySpending {
  month: string;
  [rootCategoryId: string]: number | string;
}

export interface RootCategoryInfo {
  id: string;
  name: string;
  color: string;
}

export interface SpendingTrendData {
  data: MonthlyRootCategorySpending[];
  rootCategories: RootCategoryInfo[];
}

const monthFormatter = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
});

function getMonthsInRange(dateRange: DateRange): string[] {
  const months: string[] = [];
  const startDate = new Date(dateRange.start + 'T00:00:00');
  const endDate = new Date(dateRange.end + 'T00:00:00');

  const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  while (current <= end) {
    const formatted = monthFormatter.format(current);
    months.push(formatted.slice(0, 7));
    current.setMonth(current.getMonth() + 1);
  }

  return months;
}

export function useSpendingTrendByRootCategory(): SpendingTrendData {
  const transactions = useAppStore((state) => state.transactions);
  const categories = useAppStore((state) => state.categories);
  const dateRange = useReportStore((state) => state.dateRange);
  const selectedCategoryIds = useReportStore(
    (state) => state.selectedCategoryIds
  );

  return useMemo(() => {
    const months = getMonthsInRange(dateRange);
    const selectedIdSet = new Set(selectedCategoryIds);

    const displayCategories: RootCategoryInfo[] =
      selectedCategoryIds.length > 0
        ? selectedCategoryIds
            .map((id, index) => {
              const cat = categories.find((c) => c.id === id);
              return cat
                ? {
                    id: cat.id,
                    name: cat.name,
                    color:
                      CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length],
                  }
                : null;
            })
            .filter((c): c is RootCategoryInfo => c !== null)
        : getRootCategories(categories).map((cat, index) => ({
            id: cat.id,
            name: cat.name,
            color: CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length],
          }));

    const allowedCategoryIds = new Set<string>();
    if (selectedCategoryIds.length > 0) {
      selectedCategoryIds.forEach((id) => {
        allowedCategoryIds.add(id);
        const descendants = getCategoryDescendants(categories, id);
        descendants.forEach((d) => allowedCategoryIds.add(d.id));
      });
    }

    const monthlyData: Record<string, MonthlyRootCategorySpending> = {};
    months.forEach((month) => {
      const entry: MonthlyRootCategorySpending = { month };
      displayCategories.forEach((cat) => {
        entry[cat.id] = 0;
      });
      monthlyData[month] = entry;
    });

    const categoryMap = new Map(categories.map((c) => [c.id, c]));

    transactions.forEach((transaction) => {
      if (transaction.type !== 'expense') return;
      if (transaction.excludedFromBudget) return;
      if (
        allowedCategoryIds.size > 0 &&
        transaction.categoryId &&
        !allowedCategoryIds.has(transaction.categoryId)
      )
        return;

      const transactionMonth = transaction.date.slice(0, 7);
      if (!monthlyData[transactionMonth]) return;

      const targetCategoryId = getTargetCategoryId(
        transaction.categoryId,
        categories,
        selectedIdSet,
        categoryMap
      );
      if (!targetCategoryId) return;
      if (!displayCategories.some((c) => c.id === targetCategoryId)) return;

      const currentValue =
        (monthlyData[transactionMonth][targetCategoryId] as number) || 0;
      monthlyData[transactionMonth][targetCategoryId] =
        currentValue + Math.abs(transaction.amount);
    });

    const data = months.map((month) => monthlyData[month]);

    return {
      data,
      rootCategories: displayCategories,
    };
  }, [transactions, categories, dateRange, selectedCategoryIds]);
}
